/**
 * Constants representing the possible states of an asynchronous request
 */
export const REQUEST_STATUS = {
  NOT_CALLED: "NOT_CALLED",
  IN_PROGRESS: "IN_PROGRESS",
  SUCCESS: "SUCCESS",
  FAILED: "FAILED",
} as const;

/**
 * Represents the state of an asynchronous request with type-safe states and parameters tracking
 * @template TParams - The type of the request parameters
 * @template TData - The type of successful response data
 * @template TError - The type of error that can occur
 */
export type RequestState<TParams, TData, TError> =
  | { status: typeof REQUEST_STATUS.NOT_CALLED }
  | { status: typeof REQUEST_STATUS.IN_PROGRESS; params: TParams }
  | { status: typeof REQUEST_STATUS.SUCCESS; params: TParams; data: TData }
  | { status: typeof REQUEST_STATUS.FAILED; params: TParams; error: TError };

/**
 * Utility functions for working with RequestState
 * Provides type-safe creation, manipulation, and inspection of request states
 */
export const RequestStateUtils = {
  /**
   * Creates an initial NOT_CALLED state
   * @returns A RequestState in NOT_CALLED status
   */
  notCalled: (): RequestState<never, never, never> => ({
    status: REQUEST_STATUS.NOT_CALLED,
  }),

  /**
   * Creates an IN_PROGRESS state with the given parameters
   * @param params - The parameters used for the request
   * @returns A RequestState in IN_PROGRESS status
   */
  inProgress: <TParams>(
    params: TParams
  ): RequestState<TParams, never, never> => ({
    status: REQUEST_STATUS.IN_PROGRESS,
    params,
  }),

  /**
   * Creates a SUCCESS state with the given parameters and data
   * @param params - The parameters used for the successful request
   * @param data - The successful response data
   * @returns A RequestState in SUCCESS status
   */
  success: <TParams, TData>(
    params: TParams,
    data: TData
  ): RequestState<TParams, TData, never> => ({
    status: REQUEST_STATUS.SUCCESS,
    params,
    data,
  }),

  /**
   * Creates a FAILED state with the given parameters and error
   * @param params - The parameters used for the failed request
   * @param error - The error that occurred
   * @returns A RequestState in FAILED status
   */
  failed: <TParams, TError>(
    params: TParams,
    error: TError
  ): RequestState<TParams, never, TError> => ({
    status: REQUEST_STATUS.FAILED,
    params,
    error,
  }),

  /**
   * Type guard to check if the state is NOT_CALLED
   * @param state - The state to check
   * @returns True if the state is NOT_CALLED
   */
  isNotCalled: <TParams, TData, TError>(
    state: RequestState<TParams, TData, TError>
  ): state is { status: typeof REQUEST_STATUS.NOT_CALLED } =>
    state.status === REQUEST_STATUS.NOT_CALLED,

  /**
   * Type guard to check if the state is IN_PROGRESS
   * @param state - The state to check
   * @returns True if the state is IN_PROGRESS
   */
  isInProgress: <TParams, TData, TError>(
    state: RequestState<TParams, TData, TError>
  ): state is { status: typeof REQUEST_STATUS.IN_PROGRESS; params: TParams } =>
    state.status === REQUEST_STATUS.IN_PROGRESS,

  /**
   * Type guard to check if the state is SUCCESS
   * @param state - The state to check
   * @returns True if the state is SUCCESS
   */
  isSuccess: <TParams, TData, TError>(
    state: RequestState<TParams, TData, TError>
  ): state is {
    status: typeof REQUEST_STATUS.SUCCESS;
    params: TParams;
    data: TData;
  } => state.status === REQUEST_STATUS.SUCCESS,

  /**
   * Type guard to check if the state is FAILED
   * @param state - The state to check
   * @returns True if the state is FAILED
   */
  isFailed: <TParams, TData, TError>(
    state: RequestState<TParams, TData, TError>
  ): state is {
    status: typeof REQUEST_STATUS.FAILED;
    params: TParams;
    error: TError;
  } => state.status === REQUEST_STATUS.FAILED,

  /**
   * Safely retrieves the data from a SUCCESS state or returns a default value
   * @param state - The state to get data from
   * @param defaultValue - The default value to return if state is not SUCCESS
   * @returns The success data or default value
   */
  getData: <TParams, TData, TError>(
    state: RequestState<TParams, TData, TError>,
    defaultValue: TData
  ): TData =>
    state.status === REQUEST_STATUS.SUCCESS ? state.data : defaultValue,

  /**
   * Safely retrieves the error from a FAILED state or returns a default value
   * @param state - The state to get error from
   * @param defaultValue - The default value to return if state is not FAILED
   * @returns The error or default value
   */
  getError: <TParams, TData, TError>(
    state: RequestState<TParams, TData, TError>,
    defaultValue: TError
  ): TError =>
    state.status === REQUEST_STATUS.FAILED ? state.error : defaultValue,

  /**
   * Safely retrieves the parameters from any state or returns a default value
   * @param state - The state to get parameters from
   * @param defaultValue - The default value to return if state is NOT_CALLED
   * @returns The parameters or default value
   */
  getParams: <TParams, TData, TError>(
    state: RequestState<TParams, TData, TError>,
    defaultValue: TParams
  ): TParams =>
    state.status === REQUEST_STATUS.NOT_CALLED ? defaultValue : state.params,

  /**
   * Checks if the parameters have changed from the current state
   * @param state - The current state
   * @param newParams - The new parameters to compare against
   * @returns True if parameters have changed or state is NOT_CALLED
   */
  haveParamsChanged: <TParams, TData, TError>(
    state: RequestState<TParams, TData, TError>,
    newParams: TParams
  ): boolean => {
    if (state.status === REQUEST_STATUS.NOT_CALLED) return true;
    return JSON.stringify(state.params) !== JSON.stringify(newParams);
  },

  /**
   * Maps the data of a SUCCESS state to a new value while preserving the state
   * @param state - The state to map
   * @param mapper - Function to transform the success data
   * @returns A new state with transformed data if SUCCESS, otherwise the original state
   */
  map: <TParams, TData, TError, TNewData>(
    state: RequestState<TParams, TData, TError>,
    mapper: (data: TData) => TNewData
  ): RequestState<TParams, TNewData, TError> => {
    if (state.status === REQUEST_STATUS.SUCCESS) {
      return RequestStateUtils.success(state.params, mapper(state.data));
    }
    return state as RequestState<TParams, TNewData, TError>;
  },
};
